import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { API } from "../lib/api";

const api = (path, opts = {}) =>
  fetch(`${API}${path}`, { credentials: "include", ...opts }).then(async (r) => {
    if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || "Errore"); }
    return r.json();
  });

const CTA_MODES = [
  { k: "application", l: "Solo candidatura" },
  { k: "direct", l: "Acquisto diretto" },
  { k: "apply_pay", l: "Candidatura + pagamento dopo approvazione" },
  { k: "coming_soon", l: "Coming soon / Non disponibile" },
];
const STATUSES = [
  { k: "active", l: "Attivo" },
  { k: "coming_soon", l: "Coming soon" },
  { k: "closed", l: "Iscrizioni chiuse" },
];

const eur2c = (v) => (v === "" || v === null || v === undefined ? null : Math.round(parseFloat(v) * 100));
const c2eur = (c) => (c === null || c === undefined ? "" : String(c / 100));

export default function AdminAcademy() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await api("/admin/academy"); setCourses(d.items || []); }
    catch (e) { toast.error(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-academy">
      <p className="text-sm text-[var(--ink-soft)] mb-6 max-w-3xl leading-relaxed">
        Gestisci lo stato di vendita di ogni Executive Program. I pagamenti restano disattivati finché non scegli
        "Acquisto diretto" o "Candidatura + pagamento": in quel caso il prezzo Stripe viene creato automaticamente
        dal sistema (ambiente di test, nessuna vendita live).
      </p>
      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[var(--petrol)]" /></div>
      ) : (
        <div className="space-y-6">{courses.map((c) => <CourseCard key={c.slug} c={c} reload={load} />)}</div>
      )}
    </div>
  );
}

function CourseCard({ c, reload }) {
  const [f, setF] = useState({
    cta_mode: c.cta_mode || "application", cta_text: c.cta_text || "", status: c.status || "active",
    price_standard: c2eur(c.price_standard), price_current: c2eur(c.price_current), price_promo: c2eur(c.price_promo),
    promo_start: c.promo_start || "", promo_end: c.promo_end || "",
    enroll_open: c.enroll_open || "", enroll_close: c.enroll_close || "",
    seats_total: c.seats_total ?? "", currency: c.currency || "eur", vat_included: c.vat_included !== false,
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      const r = await api(`/admin/academy/${c.slug}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cta_mode: f.cta_mode, cta_text: f.cta_text, status: f.status,
          price_standard: eur2c(f.price_standard), price_current: eur2c(f.price_current), price_promo: eur2c(f.price_promo),
          promo_start: f.promo_start || null, promo_end: f.promo_end || null,
          enroll_open: f.enroll_open || null, enroll_close: f.enroll_close || null,
          seats_total: f.seats_total === "" ? null : parseInt(f.seats_total, 10),
          currency: f.currency, vat_included: f.vat_included,
        }),
      });
      if (r.stripe_warning) toast.warning(r.stripe_warning);
      else toast.success(`${c.title} aggiornato`);
      reload();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const modeLabel = CTA_MODES.find((m) => m.k === c.cta_mode)?.l || c.cta_mode;

  return (
    <div className="bg-white border border-[var(--line)] p-6" data-testid={`academy-card-${c.slug}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
        <div>
          <h3 className="font-serif text-xl text-[var(--ink)]">{c.title}</h3>
          <div className="text-xs text-[var(--ink-soft)] mt-1">/{c.slug}</div>
        </div>
        <span className="text-xs uppercase tracking-[0.12em] border border-[var(--gold)] text-[var(--accent)] px-3 py-1" data-testid={`academy-mode-${c.slug}`}>{modeLabel}</span>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <label className="block">
          <span className="overline !text-[var(--anthracite)] block mb-1.5">Stato CTA</span>
          <select data-testid={`academy-cta-mode-${c.slug}`} value={f.cta_mode} onChange={set("cta_mode")}
            className="w-full border border-[var(--line)] px-3 py-2 text-sm bg-white outline-none focus:border-[var(--petrol)]">
            {CTA_MODES.map((m) => <option key={m.k} value={m.k}>{m.l}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="overline !text-[var(--anthracite)] block mb-1.5">Stato corso</span>
          <select data-testid={`academy-status-${c.slug}`} value={f.status} onChange={set("status")}
            className="w-full border border-[var(--line)] px-3 py-2 text-sm bg-white outline-none focus:border-[var(--petrol)]">
            {STATUSES.map((s) => <option key={s.k} value={s.k}>{s.l}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="overline !text-[var(--anthracite)] block mb-1.5">Testo CTA (facoltativo)</span>
          <input data-testid={`academy-cta-text-${c.slug}`} maxLength={40} placeholder="Es. Candidati alla prossima edizione" value={f.cta_text} onChange={set("cta_text")}
            className="w-full border border-[var(--line)] px-3 py-2 text-sm outline-none focus:border-[var(--petrol)]" />
        </label>

        <label className="block">
          <span className="overline !text-[var(--anthracite)] block mb-1.5">Prezzo standard €</span>
          <input data-testid={`academy-price-standard-${c.slug}`} type="number" step="0.01" value={f.price_standard} onChange={set("price_standard")}
            className="w-full border border-[var(--line)] px-3 py-2 text-sm outline-none focus:border-[var(--petrol)]" />
        </label>
        <label className="block">
          <span className="overline !text-[var(--anthracite)] block mb-1.5">Prezzo attuale €</span>
          <input data-testid={`academy-price-current-${c.slug}`} type="number" step="0.01" value={f.price_current} onChange={set("price_current")}
            className="w-full border border-[var(--line)] px-3 py-2 text-sm outline-none focus:border-[var(--petrol)]" />
        </label>
        <label className="block">
          <span className="overline !text-[var(--anthracite)] block mb-1.5">Prezzo promo € (facolt.)</span>
          <input data-testid={`academy-price-promo-${c.slug}`} type="number" step="0.01" value={f.price_promo} onChange={set("price_promo")}
            className="w-full border border-[var(--line)] px-3 py-2 text-sm outline-none focus:border-[var(--petrol)]" />
        </label>

        <label className="block">
          <span className="overline !text-[var(--anthracite)] block mb-1.5">Inizio promo</span>
          <input data-testid={`academy-promo-start-${c.slug}`} type="date" value={f.promo_start} onChange={set("promo_start")}
            className="w-full border border-[var(--line)] px-3 py-2 text-sm outline-none focus:border-[var(--petrol)]" />
        </label>
        <label className="block">
          <span className="overline !text-[var(--anthracite)] block mb-1.5">Fine promo</span>
          <input data-testid={`academy-promo-end-${c.slug}`} type="date" value={f.promo_end} onChange={set("promo_end")}
            className="w-full border border-[var(--line)] px-3 py-2 text-sm outline-none focus:border-[var(--petrol)]" />
        </label>
        <label className="block">
          <span className="overline !text-[var(--anthracite)] block mb-1.5">Posti disponibili</span>
          <input data-testid={`academy-seats-${c.slug}`} type="number" min="0" value={f.seats_total} onChange={set("seats_total")}
            className="w-full border border-[var(--line)] px-3 py-2 text-sm outline-none focus:border-[var(--petrol)]" />
        </label>

        <label className="block">
          <span className="overline !text-[var(--anthracite)] block mb-1.5">Apertura iscrizioni</span>
          <input data-testid={`academy-enroll-open-${c.slug}`} type="date" value={f.enroll_open} onChange={set("enroll_open")}
            className="w-full border border-[var(--line)] px-3 py-2 text-sm outline-none focus:border-[var(--petrol)]" />
        </label>
        <label className="block">
          <span className="overline !text-[var(--anthracite)] block mb-1.5">Chiusura iscrizioni</span>
          <input data-testid={`academy-enroll-close-${c.slug}`} type="date" value={f.enroll_close} onChange={set("enroll_close")}
            className="w-full border border-[var(--line)] px-3 py-2 text-sm outline-none focus:border-[var(--petrol)]" />
        </label>
        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <span className="overline !text-[var(--anthracite)] block mb-1.5">Valuta</span>
            <select data-testid={`academy-currency-${c.slug}`} value={f.currency} onChange={set("currency")}
              className="w-full border border-[var(--line)] px-3 py-2 text-sm bg-white outline-none focus:border-[var(--petrol)]">
              <option value="eur">EUR</option><option value="usd">USD</option><option value="gbp">GBP</option>
            </select>
          </label>
          <label className="block">
            <span className="overline !text-[var(--anthracite)] block mb-1.5">IVA</span>
            <select data-testid={`academy-vat-${c.slug}`} value={f.vat_included ? "incl" : "excl"}
              onChange={(e) => setF({ ...f, vat_included: e.target.value === "incl" })}
              className="w-full border border-[var(--line)] px-3 py-2 text-sm bg-white outline-none focus:border-[var(--petrol)]">
              <option value="incl">Inclusa</option><option value="excl">Esclusa</option>
            </select>
          </label>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-4 border-t border-[var(--line)] pt-5">
        <button data-testid={`academy-save-${c.slug}`} onClick={save} disabled={busy}
          className="btn-fill inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold bg-[var(--petrol)] text-white px-6 py-3 disabled:opacity-60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} /> Salva</>}
        </button>
        <span className="text-xs text-[var(--ink-soft)]" data-testid={`academy-stripe-${c.slug}`}>
          Stripe Price ID: {c.stripe_price_id || "— (creato automaticamente all'attivazione della vendita)"}
        </span>
      </div>
    </div>
  );
}
